import { z } from 'zod'

/**
 * `LGH-040`. The customer console's own login — `customer-app`'s
 * `pruvato_tenant_session` cookie, called directly (`DR-033`: same-site,
 * cross-origin, no proxy through this console's own backend). Distinct from
 * `auth.ts`'s `meResponseSchema`, which is the OPERATOR's session — one
 * `admin` role over every tenant via a switcher. This is a real tenant
 * member, scoped to exactly one tenant, no switcher, no `admin` role.
 *
 * Wire shape transcribed from `REQ-platform-core-the-tenant-session-shape-
 * for-console`'s own answer, not from a contract file this repo can read —
 * `customer-app` isn't in Lighthouse's whitelist. Treat as the best-faith
 * reconstruction of what was described in prose; verify against the real
 * endpoint before relying on it in a live check.
 */
export const tenantMemberRoleSchema = z.enum(['ADMIN', 'MANAGER', 'SALES'])
export type TenantMemberRole = z.infer<typeof tenantMemberRoleSchema>

/**
 * `email`/`name` are present on `/me`; `verify-otp`'s own response omits them
 * (the client already knows the phone it typed) — both optional here so one
 * type serves both responses without a caller assuming a field that may be
 * missing.
 *
 * **`nullable()`, not just `optional()` — `TST-001` finding 4.** `users.email`
 * is nullable on the platform (a phone-only invitee has none — `PLT-086`'s
 * own "the one real schema change" for `LGH-040`'s accept flow), and `/me`
 * answers `email: null` for exactly that member, not an omitted key. A
 * schema that only allows `undefined` refuses every invite-minted member's
 * own session read — the entire population `LGH-040` exists to create.
 * `name` gets the same treatment defensively, for the same reason.
 */
export const tenantAuthUserSchema = z.object({
  id: z.string(),
  role: tenantMemberRoleSchema,
  email: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
})

export const tenantAuthMeSchema = z.object({
  user: tenantAuthUserSchema,
  tenant: z.object({ id: z.string() }),
})
export type TenantAuthMe = z.infer<typeof tenantAuthMeSchema>

export const requestTenantOtpRequestSchema = z.object({ phone: z.string().min(1) })

export const verifyTenantOtpRequestSchema = z.object({
  phone: z.string().min(1),
  code: z.string().min(1),
})

/**
 * `wrong-code` is deliberately indistinguishable from an unknown phone —
 * `PLT-086`'s own enumeration-safety reasoning. `no-code-requested` and
 * `expired`/`already-used` are the other three named error codes; all four
 * arrive as 401 with this shape.
 */
export const tenantAuthErrorCodeSchema = z.enum([
  'wrong-code',
  'no-code-requested',
  'expired',
  'already-used',
])

/**
 * `LGH-050`, `PLT-106`. The reverse of the above — the console mints a code
 * and shows it; the member sends it back over WhatsApp themselves. Wire
 * shape transcribed from `REQ-platform-core-the-reverse-login-code-wire-
 * shape-for-console`'s own answer, same "not a contract file this repo can
 * read" caveat as `tenantAuthMeSchema` above.
 *
 * `request-login-code` always returns a real code, unlike `request-otp` —
 * `1C`'s own report is explicit that this is not enumeration-unsafe here,
 * since nothing is sent OUT to the caller-supplied phone in this flow.
 */
export const requestLoginCodeRequestSchema = z.object({ phone: z.string().min(1) })

export const requestLoginCodeResponseSchema = z.object({
  code: z.string(),
  deepLink: z.string(),
  expiresAt: z.string(),
})
export type RequestLoginCodeResponse = z.infer<typeof requestLoginCodeResponseSchema>

/**
 * `pending`/`expired` from the poll endpoint's own JSON `status` field.
 * A 404 (a bogus code, or a code already claimed by an earlier poll) is
 * NOT part of this schema — `1C`'s own answer says to treat it the same as
 * `expired` for UI purposes, so `pollLoginCode` (`tenant-auth/api.ts`) maps
 * that HTTP status to this same `expired` member rather than throwing,
 * mirroring `fetchTenantMe`'s null-on-401 convention.
 */
export const loginCodeStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('pending') }),
  z.object({ status: z.literal('expired') }),
  z.object({
    status: z.literal('confirmed'),
    user: tenantAuthUserSchema,
    tenant: z.object({ id: z.string() }),
  }),
])
export type LoginCodeStatus = z.infer<typeof loginCodeStatusSchema>
